import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { AlertTriangle, ChevronDown, ChevronUp, Shield, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";

/**
 * GovernanceNotice - Displays mandatory governance notices at key touchpoints.
 * @param {string} location - Where to show: "global", "submissions", "enforcement", "drc", "ai_analysis"
 * @param {boolean} requireAcknowledgment - Whether user must scroll/acknowledge before proceeding
 * @param {function} onAcknowledged - Callback when user acknowledges
 * @param {boolean} compact - Compact mode (collapsible)
 */
const GovernanceNotice = ({ location = "global", requireAcknowledgment = false, onAcknowledged, compact = false }) => {
  const { token } = useAuth();
  const [notices, setNotices] = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [expanded, setExpanded] = useState(!compact);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const res = await axios.get(`${API}/governance-notices/by-location/${location}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotices(res.data || []);
      } catch {
        // Use inline defaults if API unavailable
        setNotices([]);
      }
    };
    if (token) fetchNotices();
  }, [token, location]);

  const handleAcknowledge = () => {
    setAcknowledged(true);
    onAcknowledged?.();
  };

  if (notices.length === 0) return null;

  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-lg overflow-hidden" data-testid={`governance-notice-${location}`}>
      <button
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-amber-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid="governance-notice-toggle"
      >
        <Shield className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <span className="text-sm font-medium text-amber-800 flex-1">Governance Notice</span>
        {compact && (expanded ? <ChevronUp className="w-4 h-4 text-amber-600" /> : <ChevronDown className="w-4 h-4 text-amber-600" />)}
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-3">
          {notices.map((notice, i) => (
            <div key={notice.notice_id || i} className="text-sm text-amber-900/80 leading-relaxed">
              {notice.title && notice.title !== "Governing Document Hierarchy" && (
                <p className="font-medium text-amber-800 mb-1">{notice.title}</p>
              )}
              <div className="whitespace-pre-line">{notice.content}</div>
            </div>
          ))}

          {requireAcknowledgment && !acknowledged && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={handleAcknowledge}
              data-testid="governance-acknowledge-btn"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              I Acknowledge
            </Button>
          )}
          {acknowledged && (
            <div className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3 h-3" />
              Acknowledged
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GovernanceNotice;
